import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  X, Send, Loader2, Bold, Italic, Underline as UnderlineIcon,
  List, ListOrdered, Quote, Link2, Heading1, Heading2,
  Undo, Redo, Code, Minus, AlertTriangle, Plus,
} from 'lucide-react';
import { Button } from './ui/button';
import RecipientInput from './RecipientInput';
import { joinAddresses, parseAddress } from '../lib/emailAddresses';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── Toolbar ───────────────────────────────────────────────────────────
function ToolbarButton({ onClick, active, disabled, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded-md transition-colors disabled:opacity-30 ${
        active
          ? 'bg-primary/15 text-primary'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }) {
  const setLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Enter URL', editor.getAttributes('link').href || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const groups = [
    [
      { icon: <Bold className="w-3.5 h-3.5" />,    cmd: () => editor.chain().focus().toggleBold().run(),   active: editor.isActive('bold'),   title: 'Bold (⌘B)' },
      { icon: <Italic className="w-3.5 h-3.5" />,  cmd: () => editor.chain().focus().toggleItalic().run(), active: editor.isActive('italic'), title: 'Italic (⌘I)' },
      { icon: <UnderlineIcon className="w-3.5 h-3.5" />, cmd: () => editor.chain().focus().toggleStrike().run(), active: editor.isActive('strike'), title: 'Strike-through' },
      { icon: <Code className="w-3.5 h-3.5" />,    cmd: () => editor.chain().focus().toggleCode().run(),   active: editor.isActive('code'),   title: 'Inline code' },
    ],
    [
      { icon: <Heading1 className="w-3.5 h-3.5" />, cmd: () => editor.chain().focus().toggleHeading({ level: 1 }).run(), active: editor.isActive('heading', { level: 1 }), title: 'Heading 1' },
      { icon: <Heading2 className="w-3.5 h-3.5" />, cmd: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active: editor.isActive('heading', { level: 2 }), title: 'Heading 2' },
    ],
    [
      { icon: <List className="w-3.5 h-3.5" />,        cmd: () => editor.chain().focus().toggleBulletList().run(),  active: editor.isActive('bulletList'),  title: 'Bulleted list' },
      { icon: <ListOrdered className="w-3.5 h-3.5" />, cmd: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive('orderedList'), title: 'Numbered list' },
      { icon: <Quote className="w-3.5 h-3.5" />,       cmd: () => editor.chain().focus().toggleBlockquote().run(),  active: editor.isActive('blockquote'),  title: 'Quote' },
      { icon: <Minus className="w-3.5 h-3.5" />,       cmd: () => editor.chain().focus().setHorizontalRule().run(),                                       title: 'Divider' },
    ],
    [
      { icon: <Link2 className="w-3.5 h-3.5" />, cmd: setLink, active: editor.isActive('link'), title: 'Link' },
    ],
    [
      { icon: <Undo className="w-3.5 h-3.5" />, cmd: () => editor.chain().focus().undo().run(), title: 'Undo' },
      { icon: <Redo className="w-3.5 h-3.5" />, cmd: () => editor.chain().focus().redo().run(), title: 'Redo' },
    ],
  ];

  return (
    <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-b border-border bg-secondary/30" data-testid="compose-toolbar">
      {groups.map((g, gi) => (
        <React.Fragment key={gi}>
          {g.map((b, bi) => (
            <ToolbarButton key={bi} onClick={b.cmd} active={b.active} title={b.title}>
              {b.icon}
            </ToolbarButton>
          ))}
          {gi < groups.length - 1 && <span className="w-px h-4 bg-border mx-1" />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Compose Modal ─────────────────────────────────────────────────────
export default function ComposeModal({
  open,
  onClose,
  initial = {},
  onSent,
}) {
  // Helper: turn whatever the caller passed (string or array) into a clean list
  const toList = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v.map(parseAddress).filter(Boolean);
    return v.split(/[,;]/).map(parseAddress).filter(Boolean);
  };

  const [to, setTo]           = useState(toList(initial.to));
  const [cc, setCc]           = useState(toList(initial.cc));
  const [showCc, setShowCc]   = useState(toList(initial.cc).length > 0);
  const [subject, setSubject] = useState(initial.subject || '');
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState(null);
  const [showNote, setShowNote] = useState(false);

  // Suggestions = thread participants minus already-added addresses
  const ccSuggestions = (initial.thread_participants || []).filter(
    p => !cc.includes(p) && !to.includes(p)
  );
  const toSuggestions = (initial.thread_participants || []).filter(
    p => !to.includes(p) && !cc.includes(p)
  );

  // Pre-formatted audit body — seeded into a contentEditable region so the
  // user can tweak text before sending while keeping the inline-styled tables
  // intact. Tiptap can't round-trip our custom inline styles without flattening
  // them, so we use a plain contentEditable div as the editor surface.
  const auditBodyHtml = initial.body_html || '';
  const auditBodyRef  = useRef(null);

  // Seed the editable region once whenever it (re)opens with new content.
  useEffect(() => {
    if (open && auditBodyRef.current) {
      auditBodyRef.current.innerHTML = auditBodyHtml || '<p>No audit body.</p>';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, auditBodyHtml]);

  // Personal-note editor (optional preamble that goes ABOVE the audit body)
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', class: 'text-primary underline' },
      }),
      Placeholder.configure({ placeholder: 'Add an optional personal note above the audit report…' }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none px-4 py-3 min-h-[120px] focus:outline-none',
        'data-testid': 'compose-editor',
      },
    },
  });

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setTo(toList(initial.to));
      setCc(toList(initial.cc));
      setShowCc(toList(initial.cc).length > 0);
      setSubject(initial.subject || '');
      setError(null);
      setShowNote(false);
      if (editor) editor.commands.setContent('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(initial)]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, sending, onClose]);

  const handleSend = async () => {
    if (!to.length)      { setError('At least one recipient (To) is required.'); return; }
    if (!subject.trim()) { setError('Subject is required.');   return; }

    const noteHtml = editor?.getHTML() || '';
    const hasNote  = !!noteHtml.trim() && noteHtml !== '<p></p>';
    // Read the (possibly edited) audit body from the contentEditable region.
    const editedAuditHtml = auditBodyRef.current?.innerHTML?.trim() || auditBodyHtml;
    // Final HTML = optional note + edited audit body
    const body_html = hasNote
      ? `${noteHtml}<br/>${editedAuditHtml}`
      : editedAuditHtml;

    if (!body_html.trim()) {
      setError('Message body is empty.'); return;
    }

    setSending(true); setError(null);
    try {
      const r = await fetch(`${BACKEND}/api/gmail/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to:           joinAddresses(to),
          cc:           cc.length ? joinAddresses(cc) : null,
          subject,
          body_html,
          in_reply_to:  initial.in_reply_to || null,
          references:   initial.references  || null,
          thread_id:    initial.thread_id   || null,
          audit_id:     initial.audit_id    || null,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data.detail || `HTTP ${r.status}`);
      }
      toast.success(`Email sent to ${to.length} recipient${to.length > 1 ? 's' : ''}${cc.length ? ` + ${cc.length} on CC` : ''}`);
      onSent?.(data);
      onClose();
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
        onClick={() => !sending && onClose()}
        data-testid="compose-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 32, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-3xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col"
          style={{ maxHeight: '92vh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-gradient-to-r from-primary/8 to-transparent rounded-t-2xl">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
                <Send className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h2 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>New email</h2>
                <p className="text-[11px] text-muted-foreground">
                  Sent via your connected Gmail
                </p>
              </div>
            </div>
            <button
              type="button" onClick={onClose} disabled={sending}
              className="p-1 rounded-md hover:bg-slate-100 transition-colors disabled:opacity-40"
              data-testid="compose-close-button"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Recipients */}
          <div className="px-5 py-3 border-b border-border space-y-2.5">
            <div className="flex items-start gap-2">
              <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground w-12 flex-shrink-0 mt-1.5">To</label>
              <RecipientInput
                value={to}
                onChange={setTo}
                placeholder="recipient@company.com"
                disabled={sending}
                dataTestId="compose-to-input"
                suggestions={toSuggestions}
              />
              {!showCc && (
                <button
                  type="button"
                  onClick={() => setShowCc(true)}
                  className="text-[11px] text-muted-foreground hover:text-primary mt-1.5"
                  data-testid="compose-cc-toggle"
                >
                  Add Cc
                </button>
              )}
            </div>
            {showCc && (
              <div className="flex items-start gap-2">
                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground w-12 flex-shrink-0 mt-1.5">Cc</label>
                <RecipientInput
                  value={cc}
                  onChange={setCc}
                  placeholder="cc@company.com"
                  disabled={sending}
                  dataTestId="compose-cc-input"
                  suggestions={ccSuggestions}
                />
                <button
                  type="button"
                  onClick={() => { setShowCc(false); setCc([]); }}
                  className="text-[11px] text-muted-foreground hover:text-red-600 mt-1.5"
                >
                  Remove
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground w-12 flex-shrink-0">Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject…"
                className="flex-1 text-sm bg-transparent border-none focus:outline-none focus:ring-0 font-semibold placeholder:font-normal placeholder:text-muted-foreground/40"
                data-testid="compose-subject-input"
                disabled={sending}
              />
            </div>
          </div>

          {/* Body — optional note + read-only audit preview */}
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto bg-white">
            {/* Personal note (collapsed by default) */}
            <div className="border-b border-border">
              {showNote ? (
                <>
                  <div className="flex items-center justify-between px-4 py-2 bg-secondary/30">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      Personal note (sent above the audit report)
                    </span>
                    <button
                      type="button"
                      onClick={() => { setShowNote(false); editor?.commands.setContent(''); }}
                      className="text-[11px] text-muted-foreground hover:text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                  <Toolbar editor={editor} />
                  <div className="compose-editor-wrap">
                    <EditorContent editor={editor} />
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNote(true)}
                  className="w-full px-4 py-2.5 text-left text-xs text-muted-foreground hover:bg-secondary/40 transition-colors flex items-center gap-2 border-b border-border/40"
                  data-testid="compose-add-note-button"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span className="font-medium">Add a personal note</span>
                  <span className="text-[10px] opacity-70">(optional — sent above the audit report)</span>
                </button>
              )}
            </div>

            {/* Audit body — editable, exact-match formatting */}
            <div className="px-4 py-2 bg-emerald-50/40 border-b border-emerald-200 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">
                <Send className="w-3 h-3" />
                Audit report (editable — click anywhere to edit before sending)
              </span>
            </div>
            <div
              ref={auditBodyRef}
              contentEditable={!sending}
              suppressContentEditableWarning
              spellCheck
              className="flex-1 px-5 py-4 audit-body-preview focus:outline-none focus:ring-1 focus:ring-primary/30 focus:ring-inset"
              data-testid="compose-audit-preview"
            />
          </div>

          {/* Footer */}
          {error && (
            <div className="px-5 py-2 bg-red-50 border-t border-red-200 flex items-center gap-2 text-xs text-red-800" data-testid="compose-error">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {error}
            </div>
          )}
          <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-secondary/20">
            <div className="text-[11px] text-muted-foreground">
              Press <kbd className="px-1 py-0.5 rounded bg-white border border-border font-mono">Esc</kbd> to discard
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={sending} className="h-9 text-sm">
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !to.length || !subject.trim()}
                className="h-9 text-sm gap-2 px-5"
                data-testid="compose-send-button"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
